import { HttpClient, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { BehaviorSubject, Observable, timer } from "rxjs";
import { map, mergeMap, take, tap, timeInterval } from "rxjs/operators";
import { ConfigService } from "../../../config/config.service";
import { LoginRequest } from "../../login/login-request";
import { ResetPasswordConfirmRequest } from "../../reset-password-confirm/reset-password-confirm-request";
import { ResetPasswordRequestRequest } from "../../reset-password-request/reset-password-request-request";
import { UserToken } from "../token/token";

@Injectable({
  providedIn: "root"
})
export class AuthenticationService {
  public currentUser$: BehaviorSubject<UserToken>;

  private USER_ID_KEY: string = "user_id";
  private USER_TOKEN_KEY: string = "user_token";
  private MINUTES_BEFORE_TOKEN_EXP: number = 10;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    @Inject(LOCAL_STORAGE) private storage: WebStorageService
  ) {
    this.currentUser$ = new BehaviorSubject<UserToken>(this.initUser());
  }

  public getUserToken(): UserToken {
    let user = this.currentUser$.getValue();
    return user;
  }
  private initUser(): UserToken {
    let user: UserToken = new UserToken(
      this.storage.get(this.USER_TOKEN_KEY),
      this.storage.get(this.USER_ID_KEY));
    return user;
  }

  private saveUser() {
    this.storage.set(this.USER_ID_KEY, this.getUserToken().id);
    this.storage.set(this.USER_TOKEN_KEY, this.getUserToken().brutToken);
  }

  private eraseUser() {
    this.storage.remove(this.USER_ID_KEY);
    this.storage.remove(this.USER_TOKEN_KEY);
  }

  private setCurrentUser(user: UserToken) {
    this.currentUser$.next(user);
    this.saveUser();
    // this.scheduleRefreshToken();
  }

  private scheduleRefreshToken() {
    let when: Date = this.getUserToken().exp;
    when.setMinutes(when.getMinutes() - this.MINUTES_BEFORE_TOKEN_EXP);
    timer(when, 100).pipe(
      timeInterval(),
      map(x => x?.interval),
      take(1)
    ).subscribe(object => {
      this.refreshToken().subscribe(success => { });
    });
  }

  public login(loginRequest: LoginRequest): Observable<Object> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post(config.apiPoint + config.postLogin, loginRequest).pipe(
          tap((data: { token: string; user: number }) => {
            let user = new UserToken(data.token, data.user);
            this.setCurrentUser(user);
          })
        );
      })
    );
  }

  public refreshToken(): Observable<Object> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.get(config.apiPoint + config.refreshToken).pipe(
          tap((data: { token: string }) => {
            let user = new UserToken(data.token, this.getUserToken().id);
            this.setCurrentUser(user);
          })
        );
      })
    );
  }

  public logout() {
    this.currentUser$.next(new UserToken("", 0));
    this.eraseUser();
  }

  public confirmPasswordReset(
    request: ResetPasswordConfirmRequest
  ): Observable<HttpResponse<string>> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<string>(
          config.apiPoint + config.confirmPasswordReset,
          request,
          {
            observe: "response"
          }
        );
      })
    );
  }

  public requestPasswordReset(
    request: ResetPasswordRequestRequest
  ): Observable<HttpResponse<string>> {
    return this.configService.getConfig().pipe(
      mergeMap(config => {
        return this.http.post<string>(
          config.apiPoint + config.requestPasswordReset,
          request,
          {
            observe: "response"
          }
        );
      })
    );
  }
}
